import { useAuth } from '../lib/auth.jsx'
import { useNavigate } from 'react-router-dom'
import { Shield, Video, MessageSquare, ClipboardList, Calendar, Users, ArrowRight, Lock } from 'lucide-react'
import styles from './LandingPage.module.css'

const features = [
  { icon: Users,         title: 'Patient Profiles',        desc: 'Complete EMR with mental health history, medications, and care plans.' },
  { icon: ClipboardList, title: 'PHQ-9 & GAD-7',           desc: 'Validated screening tools with automated scoring and trend analysis.' },
  { icon: Video,         title: 'Telemed Sessions',         desc: 'HIPAA-compliant video consultations from anywhere.' },
  { icon: MessageSquare, title: 'Secure Messaging',         desc: 'End-to-end encrypted communication between patients and providers.' },
  { icon: Calendar,      title: 'Smart Scheduling',         desc: 'Intelligent appointment booking with reminders and waitlist management.' },
  { icon: Shield,        title: 'HIPAA Compliant',          desc: 'Built from the ground up to meet federal privacy and security standards.' },
]

export default function LandingPage() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/app/dashboard')
    } else {
      navigate('/login')
    }
  }

  return (
    <div className={styles.page}>
      {/* Nav */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <div className={styles.logo}>
            <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="10" fill="#0f3d5c"/>
              <path d="M24 10C24 10 14 16 14 25C14 30.5 18.5 35 24 35C29.5 35 34 30.5 34 25C34 16 24 10 24 10Z" fill="#1a8a7b" opacity="0.85"/>
              <path d="M20 24H28M24 20V28" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <div>
              <span className={styles.logoTitle}>PatientTrac</span>
              <span className={styles.logoSub}> Mind</span>
            </div>
          </div>
          <a href="/login?redirect=/app/telemed" className={styles.telemedBtn}>
            <Video size={14} />
            Start Telemed
          </a>
          <button className={styles.signInBtn} onClick={handleGetStarted}>
            <Lock size={14} />
            Secure Sign In
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Shield size={12} />
            HIPAA Compliant Mental Health Platform
          </div>
          <h1 className={styles.heroTitle}>
            Mental Health Care,<br />
            <em>Reimagined</em> for<br />
            the Digital Age
          </h1>
          <p className={styles.heroSubtitle}>
            PatientTrac Mind is a comprehensive telemed and EMR portal
            designed specifically for mental health providers — secure, compassionate, and built to HIPAA standards.
          </p>
          <div className={styles.heroCtas}>
            <button className={styles.ctaPrimary} onClick={handleGetStarted}>
              Access Portal
              <ArrowRight size={18} />
            </button>
            <a href="/login?redirect=/app/telemed" className={styles.ctaTelemed}>
              <Video size={16} />
              Start Telemed Session
            </a>
            <a href="mailto:support@patienttrac.com" className={styles.ctaSecondary}>
              Request a Demo
            </a>
          </div>
        </div>
        <div className={styles.heroVisual}>
          <div className={styles.heroCard}>
            <div className={styles.heroCardHeader}>
              <div className={styles.dot} style={{background:'#2d8a4e'}} />
              <span>Patient Overview</span>
            </div>
            <div className={styles.heroCardBody}>
              <div className={styles.statRow}>
                <span>PHQ-9 Score</span>
                <span className={styles.statBadge} style={{background:'rgba(45,138,78,0.12)',color:'#2d8a4e'}}>7 — Mild</span>
              </div>
              <div className={styles.statRow}>
                <span>GAD-7 Score</span>
                <span className={styles.statBadge} style={{background:'rgba(201,124,42,0.12)',color:'#c97c2a'}}>11 — Moderate</span>
              </div>
              <div className={styles.statRow}>
                <span>Next Session</span>
                <span className={styles.statBadge} style={{background:'rgba(26,138,123,0.12)',color:'#1a8a7b'}}>Telemed — Today</span>
              </div>
              <div className={styles.progressBar}>
                <div style={{width:'68%'}} />
              </div>
              <p className={styles.progressLabel}>Treatment Progress — 68%</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={styles.features}>
        <div className={styles.featuresInner}>
          <h2 className={styles.sectionTitle}>Everything your practice needs</h2>
          <p className={styles.sectionSub}>Built by clinicians, for clinicians — with patients always at the center.</p>
          <div className={styles.featureGrid}>
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon size={22} />
                </div>
                <h3 className={styles.featureTitle}>{title}</h3>
                <p className={styles.featureDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Ready to transform your practice?</h2>
          <p>Join providers who trust PatientTrac Mind for their mental health EMR needs.</p>
          <button className={styles.ctaPrimary} onClick={handleGetStarted}>
            Get Started Today
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className={styles.footer}>
        <p>© {new Date().getFullYear()} PatientTrac — patienttrac.com</p>
        <p className={styles.footerNote}>
          This platform is intended for licensed healthcare providers only. All data is encrypted and handled in accordance with HIPAA regulations.
        </p>
      </footer>
    </div>
  )
}
